import { createContext, useContext, useEffect, useState, useCallback } from 'react';
import * as Sentry from '@sentry/react';
import api, {
  AUTH_EXPIRED_EVENT,
  clearStoredAuthToken,
  getValidStoredAuthToken,
  setStoredAuthToken,
} from '../services/api';
import { trackAuthEvent } from '../services/analytics';

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [token, setToken] = useState(() => getValidStoredAuthToken());
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Update Sentry user context when user state changes
  useEffect(() => {
    if (user) {
      Sentry.setUser({
        id: user.id,
        email: user.email,
        username: user.name || user.businessName,
        data: { role: user.role }
      });
    } else {
      // Clear user context on logout
      Sentry.setUser(null);
    }
  }, [user]);

  // Update token in localStorage and axios headers when it changes
  useEffect(() => {
    if (token) {
      setStoredAuthToken(token);
    } else {
      clearStoredAuthToken();
    }
  }, [token]);

  useEffect(() => {
    const handleAuthExpired = () => {
      setToken(null);
      setUser(null);
      setError('Your session expired. Please log in again.');
      setLoading(false);
    };

    window.addEventListener(AUTH_EXPIRED_EVENT, handleAuthExpired);
    return () => window.removeEventListener(AUTH_EXPIRED_EVENT, handleAuthExpired);
  }, []);

  // Fetch current user data
  const fetchCurrentUser = useCallback(async (tokenOverride) => {
    const activeToken = tokenOverride || getValidStoredAuthToken();

    if (!activeToken) {
      clearStoredAuthToken();
      setToken(null);
      setUser(null);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const response = await api.get('/auth/me');
      setUser(response.data);
      setError(null);
      if (token !== activeToken) {
        setToken(activeToken);
      }
    } catch (err) {
      console.error('Failed to fetch user:', err);
      setUser(null);
      // If token is invalid, clear it
      if (err.response?.status === 401) {
        clearStoredAuthToken();
        setToken(null);
        trackAuthEvent({
          action: 'session_expiration',
          status: 'succeeded',
        });
      }
    } finally {
      setLoading(false);
    }
  }, [token]);

  // Initial user fetch
  useEffect(() => {
    fetchCurrentUser();
  }, [fetchCurrentUser]);

  // Login function
  const login = async (email, password) => {
    setLoading(true);
    setError(null);

    trackAuthEvent({
      action: 'login',
      status: 'started',
      metadata: { email_provided: !!email }
    });

    try {
      const response = await api.post('/auth/login', { email, password }, { skipAuth: true });
      const nextToken = response.data.token;

      setStoredAuthToken(nextToken);
      setToken(nextToken);
      await fetchCurrentUser(nextToken);

      trackAuthEvent({
        action: 'login',
        status: 'succeeded',
      });

      return true;
    } catch (err) {
      const errorMessage = err.response?.data?.message || 'Login failed. Please try again.';
      setError(errorMessage);
      
      trackAuthEvent({
        action: 'login',
        status: 'failed',
        errorMessage,
      });

      return false;
    } finally {
      setLoading(false);
    }
  };

  // Register function is removed as client registration is not needed

  // Logout function
  const logout = async () => {
    trackAuthEvent({
      action: 'logout',
      status: 'started'
    });

    try {
      await api.post('/auth/logout', {}, { skipAuth: true });
      
      trackAuthEvent({
        action: 'logout',
        status: 'succeeded'
      });
    } catch (err) {
      console.error('Logout error:', err);
      
      trackAuthEvent({
        action: 'logout',
        status: 'failed',
        errorMessage: err.message,
      });
    } finally {
      clearStoredAuthToken();
      setToken(null);
      setUser(null);
      // Navigation should be handled by the component that calls logout
    }
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        token,
        loading,
        error,
        isAuthenticated: !!user,
        hasRole: (role) => user?.role === role,
        login,

        logout,
        fetchCurrentUser
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
